import {createContext, useContext, useState} from 'react';
import styles from '../styles.module.scss';

/** "확인" 버튼이 포함된 Alert Modal */
const SubsEventAlertModal = ({children, onClose}) => {
  return (
    <>
      <div className={styles.contents}>{children}</div>
      <div className={styles.btn_box}>
        <button type='button' onClick={onClose}>
          확인
        </button>
      </div>
    </>
  );
};

/** "취소", "구독하기" 버튼이 포함된 Confirm Modal */
const SubsEventConfirmModal = ({children, onConfirm, onCancel}) => {
  return (
    <>
      <div className={styles.contents}>{children}</div>
      <div className={styles.btn_box}>
        <button type='button' onClick={onConfirm}>
          취소
        </button>
        <button type='button' onClick={onCancel}>
          구독하기
        </button>
      </div>
    </>
  );
};

/** 이벤트 참여 -> 이벤트 기간 내 플러스 구독 이력 없음 -> 신규결제 유도 */
const JOIN_NO_SUB_PLUS_CONFIRM = 'JOIN_NO_SUB_PLUS_CONFIRM';
/** 이벤트 참여 -> 이벤트 기간 내 플러스 구독 이력 없음 -> 결제 불가 플랫폼 */
const JOIN_NO_SUB_PLUS_ALERT = 'JOIN_NO_SUB_PLUS_ALERT';
/** 이벤트 참여 -> 이벤트 기간 내 플러스 구독 이력 있음 -> 추가결제 유도 */
const JOIN_YES_SUB_PLUS_CONFIRM = 'JOIN_YES_SUB_PLUS_CONFIRM';
/** 이벤트 참여 -> 이벤트 기간 내 플러스 구독 이력 있음 -> 결제 불가 플랫폼 */
const JOIN_YES_SUB_PLUS_ALERT = 'JOIN_YES_SUB_PLUS_ALERT';
/** 플러스 구독하기 -> 결제 불가 플랫폼 */
const BUY_SUB_PLUS_ALERT = 'BUY_SUB_PLUS_ALERT';

export const subsEventModalTypes = {
  JOIN_NO_SUB_PLUS_CONFIRM,
  JOIN_NO_SUB_PLUS_ALERT,
  JOIN_YES_SUB_PLUS_CONFIRM,
  JOIN_YES_SUB_PLUS_ALERT,
  BUY_SUB_PLUS_ALERT,
};

/**
 * 미리 정의된 모달에 대한 JSX 엘리먼트를 생성한다. (메시지 상황별로 엘리먼트 구조가 다름)
 */
const subsModalMsgFactory = (modalType, msg = '') => {
  switch (modalType) {
    case JOIN_NO_SUB_PLUS_CONFIRM:
      return (
        <>
          <p>
            이벤트에 <em>참여</em>하셨습니다.
          </p>
          <p>
            지금 바로 <strong>플러스 구독</strong>하고
            <br />
            <em>응모</em> 하시겠습니까?
          </p>
        </>
      );
    case JOIN_NO_SUB_PLUS_ALERT:
      return (
        <>
          <p>
            이벤트에 <em>참여</em>하셨습니다.
          </p>
          <p>
            해당 앱에서는 구독하고자 하는
            <br />
            스트리머의 방송국, LIVE, VOD에서 <strong>직접 구독</strong>
            하여
            <br />
            <em>추가 응모</em> 하실 수 있습니다.
          </p>
        </>
      );
    case JOIN_YES_SUB_PLUS_CONFIRM:
      return (
        <>
          <p>
            <em>응모 완료</em>되었습니다.
          </p>
          <p>
            <strong>추가 구독</strong>하여 <strong>당첨 확률</strong>을 높이실
            수 있습니다.
          </p>
        </>
      );
    case JOIN_YES_SUB_PLUS_ALERT:
      return (
        <>
          <p>
            <em>응모 완료</em>되었습니다.
            <br />
            <strong>추가 구독</strong>하여 <strong>당첨 확률</strong>을 높이실
            수 있습니다.
          </p>
          <p>
            해당 앱에서는 구독하고자 하는
            <br />
            스트리머의 방송국, LIVE, VOD에서 <strong>직접 구독</strong>하여
            <br />
            이벤트 <em>추가 응모</em> 하실 수 있습니다.
          </p>
        </>
      );
    case BUY_SUB_PLUS_ALERT:
      return (
        <>
          <p>
            해당 앱에서 구독하고자 하는
            <br />
            스트리머의 방송국, LIVE, VOD에서 <strong>직접 구독</strong>
            하여
            <br />
            이벤트 <em>추가 응모</em> 하실 수 있습니다
          </p>
        </>
      );
    default:
      return <p>{msg}</p>;
  }
};

const SubsEventModalContext = createContext({
  /**
   * Alert형 모달 노출
   * @param {*} onClose 닫기 버튼을 눌렀을 때 동작
   * @param {*} modalType 미리 정의된 모달 타입 사용 시
   * @param {*} msg 임의의 메시지 사용 시
   */
  showAlert: (onClose, modalType, msg = '') => {},
  /**
   * Confirm형 모달 노출
   * @param {*} onConfirm 확인(구독하기) 버튼을 눌렀을 때 동작
   * @param {*} onCancel 닫기 버튼을 눌렀을 때 동작
   * @param {*} modalType 미리 정의된 모달 타입 사용 시
   * @param {*} msg 임의의 메시지 사용 시
   */
  showConfirm: (onConfirm, onCancel, modalType, msg = '') => {},
});

export const useSubsEventModal = () => {
  const context = useContext(SubsEventModalContext);
  if (context === undefined) {
    throw new Error(
      'useSubsEventModal should be used within SubsEventModalProivder',
    );
  }
  return context;
};

export const SubsEventModalProvider = ({children}) => {
  const [modal, setModal] = useState(null);

  const showAlert = (onClose, modalType, msg = '') => {
    setModal({
      type: 'alert',
      content: subsModalMsgFactory(modalType, msg),
      onClose: () => {
        onClose?.();
        setModal(null);
      },
    });
  };

  const showConfirm = (onConfirm, onCancel, modalType, msg = '') => {
    setModal({
      type: 'confirm',
      content: subsModalMsgFactory(modalType, msg),
      onConfirm: () => {
        onConfirm?.();
        setModal(null);
      },
      onCancel: () => {
        onCancel?.();
        setModal(null);
      },
    });
  };

  return (
    <SubsEventModalContext.Provider value={{showAlert, showConfirm}}>
      {children}
      {!!modal && (
        <div className={styles.layer_modal_wrap}>
          <div className={styles.flex_box}>
            <div className={styles.modal_contents}>
              <div className={styles.alert_layer}>
                {modal.type === 'alert' && (
                  <SubsEventAlertModal onClose={modal.onClose}>
                    {modal.content}
                  </SubsEventAlertModal>
                )}
                {modal.type === 'confirm' && (
                  <SubsEventConfirmModal
                    onConfirm={modal.onCancel}
                    onCancel={modal.onConfirm}>
                    {modal.content}
                  </SubsEventConfirmModal>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </SubsEventModalContext.Provider>
  );
};
